import { Injectable, inject } from '@angular/core'
import { ApolloQueryResult } from '@apollo/client'
import {
  FetchRepairRequestProblemStateKeysGQL,
  FetchRepairRequestProblemStateKeysQuery,
} from '../graphql/fetch-problem-state-keys.types'
import {
  SimpleFilterConfiguration,
  SimpleFilterRepairRequestDataType,
  SimpleFilterType,
} from '../interfaces'
import { DropdownOption, I18nService } from '@wm-front/core'
import {
  RepairRequestState,
  RequestItemTypeEnum,
  UrgencyLevel,
  WorkOrderType,
} from '@wm-front/admin/graphql'
import {
  translateRepairRequestState,
  translateUrgency,
} from '@wm-front/shared/utils'
import { WorkOrderTypePipe } from '@wm-front/shared/pipes'

/**
 * Will hold the default filter configuration
 */
@Injectable({ providedIn: 'root' })
export class RepairRequestFiltersCatalogService {
  protected _fetchProblemStateKeysGql = inject(
    FetchRepairRequestProblemStateKeysGQL
  )

  protected _i18nService = inject(I18nService)

  public getConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<any>
  ): SimpleFilterConfiguration<any> {
    if (overrideConfiguration?.dataType !== undefined) {
      switch (overrideConfiguration.dataType) {
        case SimpleFilterRepairRequestDataType.URGENCY_LEVELS:
          return this.getUrgencyLevelFilterConfiguration(overrideConfiguration)
        case SimpleFilterRepairRequestDataType.CHARGEABLE:
          return this.getChargeableFilterConfiguration(overrideConfiguration)
        case SimpleFilterRepairRequestDataType.HAS_REPAIR_SUPPORT_EXPERT_ASSIGNED:
          return this.getHasRepairSupportExpertAssignedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterRepairRequestDataType.HAS_TECH_SUPPORT_EXPERT_ASSIGNED:
          return this.getHasTechSupportExpertAssignedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterRepairRequestDataType.HAS_SUPPORT_EXPERT_ASSIGNED:
          return this.getHasSupportExpertAssignedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterRepairRequestDataType.WORK_ORDER_TYPE:
          return this.getWorkOrderTypeFilterConfiguration(overrideConfiguration)
        case SimpleFilterRepairRequestDataType.REPAIR_REQUEST_TYPE:
          return this.getRequestTypesFilterConfiguration(overrideConfiguration)
        case SimpleFilterRepairRequestDataType.REPAIR_REQUEST_STATE:
          return this.getRequestStatesFilterConfiguration(overrideConfiguration)
        case SimpleFilterRepairRequestDataType.LINKED_TO_ISSUE:
          return this.getHasLinkedItemFilterConfiguration(overrideConfiguration)
        case SimpleFilterRepairRequestDataType.ONGOING_REPAIR_REQUEST_STATE:
          return this.getRequestStatesFilterConfiguration(
            overrideConfiguration,
            true
          )
        case SimpleFilterRepairRequestDataType.PROBLEM_STATE_KEYS:
          return this.getProblemStateKeysFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterRepairRequestDataType.PROBLEM_DILAP:
          return this.getProblemDilapFilterConfiguration(overrideConfiguration)
      }
    }
    return overrideConfiguration
  }

  private getUrgencyLevelFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'urgencyLevels',
      urlName: 'urgencies',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Urgency default label@@filtersCatalogUrgencyLabel:Urgence`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: Object.keys(UrgencyLevel).map((urgency) => {
        return {
          label: translateUrgency(UrgencyLevel[urgency]),
          value: UrgencyLevel[urgency],
        }
      }),
    }
  }

  private getChargeableFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'isChargeable',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Is chargeable default label@@filtersCatalogIsChargeableLabel:Facturable`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Label for chargeable option on chargeable filter@@filtersCatalogIsChargeableFilterOption:Facturable`,
          value: true,
        },
        {
          label: $localize`:Label | Label for non chargeable option on chargeable filter@@filtersCatalogIsNotChargeableFilterOption:Non facturable`,
          value: false,
        },
      ],
    }
  }

  private getRequestTypesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'itemTypes',
      urlName: 'requestTypes',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Is chargeable default label@@filtersCatalogRequestTypesLabel:Type de demande`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: [
        {
          label: $localize`:Label | Label for ManHour request type filter@@filtersCatalogManHourRequestTypeLabel:Man hour`,
          value: RequestItemTypeEnum.ManHour,
        },
        {
          label: $localize`:Label | Label for Part request type filter@@filtersCatalogPartRequestTypeLabel:Part`,
          value: RequestItemTypeEnum.Part,
        },
        {
          label: $localize`:Label | Label for Repair support request type filter@@filtersCatalogRepairSupportRequestTypeLabel:Repair support`,
          value: RequestItemTypeEnum.RepairSupport,
        },
        {
          label: $localize`:Label | Label for Tech support request type filter@@filtersCatalogTechSupportRequestTypeLabel:Technical support`,
          value: RequestItemTypeEnum.TechSupport,
        },
        {
          label: $localize`:Label | Label for ManHour request type filter@@filtersCatalogInstallationRequestTypeLabel:Installation`,
          value: RequestItemTypeEnum.Installation,
        },
      ],
      valueConverter: (data: RequestItemTypeEnum[]) => {
        if (data?.includes(RequestItemTypeEnum.Part)) {
          return [...data, RequestItemTypeEnum.ExternalPart]
        }
        return data
      },
    }
  }

  private getRequestStatesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>,
    onlyOngoing?: boolean
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'states',
      urlName: 'repairRequestStates',
      display: overrideConfiguration.display || {
        label: $localize`:Label | request states filters@@filtersCatalogRequestStatesLabel:Statut de la demande`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data:
        overrideConfiguration.data ||
        Object.keys(RepairRequestState)
          .filter((key) =>
            onlyOngoing ? !['Cancelled', 'Denied', 'Done'].includes(key) : true
          )
          .filter((key) => !['PartToInstall'].includes(key))
          .map((status) => {
            return {
              label: translateRepairRequestState(RepairRequestState[status]),
              value: RepairRequestState[status],
            }
          }),
    }
  }

  private getHasSupportExpertAssignedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'hasSupportExpertAssigned',
      display: overrideConfiguration.display || {
        label: $localize`:Label | has expert assigned filters@@filtersCatalogHasSupportExpertAssignedLabel:With expert assigned`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Label for with expert option on with expert assigned filter@@filtersCatalogHasExpertAssignedFilterOption:With expert`,
          value: true,
        },
        {
          label: $localize`:Label | Label for without expert option on with expert assigned filter@@filtersCatalogHasNoExpertAssignedFilterOption:Without expert`,
          value: false,
        },
      ],
    }
  }

  private getHasLinkedItemFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'linkedToIssues',
      display: overrideConfiguration.display || {
        label: $localize`:@@filtersCatalogInsuranceItemLabel:Insurance item`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:@@filtersCatalogInsuranceItemLinkedFilterOtion:Linked`,
          value: true,
        },
        {
          label: $localize`:@@filtersCatalogInsuranceItemNotLinkedFilterOtion:Not linked`,
          value: false,
        },
      ],
    }
  }

  private getHasRepairSupportExpertAssignedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'hasRepairSupportExpertAssigned',
      display: overrideConfiguration.display || {
        label: $localize`:Label | has repair expert assigned filters@@filtersCatalogHasRepairSupportExpertAssignedLabel:With repairer assigned`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Label for with repairer option on with repairer assigned filter@@filtersCatalogHasRepairerAssignedFilterOption:With repairer`,
          value: true,
        },
        {
          label: $localize`:Label | Label for without repairer option on with repairer assigned filter@@filtersCatalogHasNoRepairerAssignedFilterOption:Without repairer`,
          value: false,
        },
      ],
    }
  }

  private getHasTechSupportExpertAssignedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'hasTechSupportExpertAssigned',
      display: overrideConfiguration.display || {
        label: $localize`:Label | has tech expert assigned filters@@filtersCatalogHasTechSupportExpertAssignedLabel:With technician assigned`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Label for with technician option on with repairer assigned filter@@filtersCatalogHasTechnicianAssignedFilterOption:With technician`,
          value: true,
        },
        {
          label: $localize`:Label | Label for without technician option on with repairer assigned filter@@filtersCatalogHasNoRepairerTechnicianFilterOption:Without technician`,
          value: false,
        },
      ],
    }
  }

  private getWorkOrderTypeFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    const pipe = new WorkOrderTypePipe()
    return {
      key: 'workOrderTypes',
      urlName: 'workOrderTypes',
      display: overrideConfiguration.display || {
        label: $localize`:@@filtersCatalogWorkOrderTypesLabel:Type de work order`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: Object.keys(WorkOrderType)
        .filter((wot) => wot !== 'Callout')
        .map((wot) => {
          return {
            label: pipe.transform(WorkOrderType[wot]),
            value: WorkOrderType[wot],
          }
        }),
    }
  }

  private getProblemStateKeysFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchRepairRequestProblemStateKeysQuery>
  ): SimpleFilterConfiguration<FetchRepairRequestProblemStateKeysQuery> {
    return {
      key: 'problemStateKeys',
      urlName: 'problems',
      type: SimpleFilterType.DROPDOWN,
      display: overrideConfiguration.display || {
        label: $localize`:Problem state keys filter label | label for the problem state keys filter@@repairRequestsProblemStateKeysFilterLabel:Problems`,
        placeholder: $localize`:Problem state keys filter placeholder | placeholder for the problem state keys filter@@repairRequestsProblemStateKeysFilterPlaceholder:Find problem`,
      },
      dataGetter: this._fetchProblemStateKeysGql,
      dropdownOptionConverter: (
        data: ApolloQueryResult<FetchRepairRequestProblemStateKeysQuery>
      ): DropdownOption[] => {
        return data.data.repairRequestProblemStateKeys.edges.map((edge) => ({
          label: edge.node.label || edge.node.stateKey,
          value: edge.node.stateKey,
        }))
      },
      paginationConverter: (
        data: ApolloQueryResult<FetchRepairRequestProblemStateKeysQuery>
      ) => {
        const pageInfo = data.data.repairRequestProblemStateKeys.pageInfo
        return {
          hasNextPage: pageInfo.hasNextPage,
          hasPreviousPage: pageInfo.hasPreviousPage,
          startCursor: pageInfo.startCursor || '',
          endCursor: pageInfo.endCursor || '',
        }
      },
      bodyConverter: (filters, state, externalState, pagination) => {
        return {
          pagination: pagination || { first: 100 },
          languages: [this._i18nService.language],
        }
      },
      defaultPageSize: 100,
    }
  }

  private getProblemDilapFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'problemDilap',
      urlName: 'dilap',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Problem dilap default label@@filtersCatalogProblemDilapLabel:Déclaré en EDL`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Label for dilap option on problem dilap filter@@filtersCatalogProblemDilapFilterOption:Oui`,
          value: true,
        },
      ],
    }
  }
}
